import React, { useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Divider,
  Stack,
  Modal,
  Heading,
  Input,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  Button,
  Text,
  useClipboard,
  useDisclosure
} from "@chakra-ui/core";
import { FaLink } from "react-icons/fa";
import { IoIosArrowForward } from "react-icons/io";
import { MdKeyboardArrowRight } from "react-icons/md";
import { useMutation } from "react-apollo";

import CustomerDetailsForm from "./CustomerDetailsForm";
import OrderDetailsForm from "./OrderDetailsForm";
import { CREATE_TRANSACTION, CREATE_CUSTOMER } from "../../graphql/mutations";

import { parsePhoneNumberFromString as parseMobile } from "libphonenumber-js/mobile";
import currency from "currency.js";

/** Entry point to generate a new payment link */

const NewPaymentLink = ({
  customer,
  isCreateLink,
  setIsCreateLink,
  refetch,
  intialStep = "1",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ sms: true });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [step, setStep] = useState(intialStep);
  const [createTransaction] = useMutation(CREATE_TRANSACTION);
  const [createCustomer] = useMutation(CREATE_CUSTOMER);
  const [checkoutRef, setCheckoutRef] = useState("");
  const { onCopy, hasCopied } = useClipboard(checkoutRef);
  const [customerId, setCustomerId] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(customer ? customer : null);

  /** Initialise the customer ID and Info if received as props
   * Short cut from orders grid ActionList to create new
   * link bypassing intial step (1)
   */
  React.useEffect(() => {
    if (customerInfo && customerInfo.id && intialStep === "2") {
      setCustomerId(customerInfo.id);
      setFormData((prevState) => ({
        ...prevState,
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        phone: customerInfo.phone,
        email: customerInfo.email,
      }));
    }
  }, [customerInfo, intialStep]);

  const onSubmit = (event) => {
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);
    const action = event.target.id;
    // data checking
    if (!validateForm()) {
      setIsLoading(false);
      return;
    } else if (action === "add-order" && step === "1") {
      setError(null);
      /** If we have not received customer info from intialisation
       * then it means that we cillected the info from the form
       * thus assiging the formData to customer info to be passed on the next step
       */
      const { firstName, lastName, email, phone } = formData;
      const formatPhone = `${phone}`;
      createCustomer({
        variables: {
          firstName,
          lastName,
          email,
          phone: formatPhone,
        },
      })
        .then((res) => {
          setStep("2");
          setCustomerId(res.data.createCustomer.id);
        })
        .catch((err) => {
          if (err.graphQLErrors[0]) {
            setError(err.graphQLErrors[0].message);
          } else {
            setError("Unable to save customer!");
          }
        });
    }
    // else if (action === "save-customer" && step === "1") {
    //   const { firstName, lastName, email, phone } = formData;
    //   const formatPhone = `${phone}`;

    //   createCustomer({
    //     variables: {
    //       firstName,
    //       lastName,
    //       email,
    //       phone: formatPhone,
    //     },
    //   })
    //     .then((res) => setSuccessMessage("Customer saved successfully"))
    //     .catch((err) => {
    //       if (err.graphQLErrors[0]) {
    //         setError(err.graphQLErrors[0].message);
    //       } else {
    //         setError("Unable to save customer!");
    //       }
    //     });
    // }
    setIsLoading(false);
  };

  const validateForm = () => {
    if (!(formData.firstName && formData.lastName && formData.phone)) {
      setError("Please fill all the required fields");
      return false;
    }
    // validate phone number
    else if (
      parseMobile("+" + formData.phone) &&
      !parseMobile("+" + formData.phone).isPossible()
    ) {
      setError("Invalide phone number");
      return false;
    } else return true;
  };

  const validateOrderForm = () => {
    return formData.description && formData.amount;
  };

  const handleChange = ({ target: { name, value } }) => {
    setError(null);
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const initiateCreateTransaction = (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    if (!validateOrderForm()) {
      setError("Please fill all the required fields");
      setIsLoading(false);
    } else {
      const {
        firstName,
        lastName,
        email,
        amount,
        customerRef,
        description,
        phone,
      } = formData;

      amount.replace(",", ".");

      //console.log( currency(amount).intValue)
      //const floatAmount = parseFloat(parseInt(amount).toFixed(2));
      const floatAmount = currency(amount).intValue;

      const formatPhone = `${phone}`;
      createTransaction({
        variables: {
          amount: floatAmount,
          customerRef,
          description,
          firstName,
          lastName,
          email,
          currency: "EUR",
          customerId,
          phone: formatPhone,
        },
      })
        .then((res) => {
          const checkoutRef = `${window.location.origin}/checkout/${res.data.createTransaction.ref}`;
          setCheckoutRef(checkoutRef);
          let message;
          if (step === "1") {
            message = "Customer successfuly created";
          } else message = "Customer and Link successfuly created";
          setSuccessMessage(message);
        })
        .catch((err) => {
          console.log(err);
          if (err.graphQLErrors[0]) {
            setError(err.graphQLErrors[0].message);
          } else {
            setError("Unable to create transaction!");
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const reInitForm = () => {
    if (successMessage) {
      //window.location.reload();
      refetch();
    }
    setError(null);
    setStep(intialStep);
    setSuccessMessage(null);
    setFormData({ sms: true });
    setCustomerInfo(null);
    setIsCreateLink(false);
    onClose();
  };

  const { onClose } = useDisclosure();

  return (
    <Modal isCentered isOpen={isCreateLink} size="2xl" onClose={onClose}>
      <ModalOverlay />
      <form>
        <ModalContent py="20px" borderRadius="5px">
          <ModalBody color="primary.500">
            <Box mb="25px">
              <Heading
                textTransform="uppercase"
                textAlign="center"
                as="h3"
                size="lg"
              >
                Create new payment link
              </Heading>
              {customerInfo && customerInfo.id && (
                <Stack
                  borderWidth="1px"
                  w="80%"
                  m="auto"
                  my="20px"
                  alignItems="center"
                  justifyContent="center"
                  isInline
                >
                  <Box
                    color="success.400"
                    as={MdKeyboardArrowRight}
                    fontSize="48px"
                  />
                  <Text color="primary.400" fontSize="lg">
                    For :
                    <Box as="span" fontWeight="bold">
                      {" "}
                      {customerInfo.firstName} {customerInfo.lastName}
                    </Box>
                    <Box as="span" fontSize="sm">
                      {" "}
                      ({customerInfo.phone})
                    </Box>
                  </Text>
                </Stack>
              )}
              <Divider />
              {error && (
                <Box my="3">
                  <Alert status="error">
                    <AlertIcon />
                    <Stack>
                      <Text fontSize="bold">
                        There was an error processing your request
                      </Text>
                      <Text fontWeight="bold">{error}</Text>
                    </Stack>
                  </Alert>
                </Box>
              )}
              {successMessage && (
                <Box my="3">
                  <Alert status="success">
                    <AlertIcon />
                    <Stack w="100%">
                      <Text>{successMessage}</Text>
                      {step === "2" && (
                        <Stack
                          isInline
                          borderRadius="5px"
                          borderColor="success.700"
                          borderWidth="1px"
                          alignItems="center"
                          w="100%"
                        >
                          <Input
                            fontSize="sm"
                            border="none"
                            value={checkoutRef}
                            isReadOnly
                          />
                          <Button
                            variant="solid"
                            variantColor="primary"
                            size="xs"
                            mx="4px"
                            onClick={onCopy}
                            ml={2}
                          >
                            {hasCopied ? "Copied" : "Copy"}
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  </Alert>
                </Box>
              )}
            </Box>
            {step === "1" && (
              <CustomerDetailsForm
                {...{ handleChange, successMessage, setCustomerInfo, setError }}
              />
            )}
            {step === "2" && (
              <OrderDetailsForm
                customerInfo={customerInfo ? customerInfo : formData}
                setFormData={setFormData}
                successMessage={successMessage}
              />
            )}
          </ModalBody>
          <ModalFooter>
            <Stack w="90%" justifyContent="center" alignItems="center">
              {step === "1" && !successMessage && (
                <Stack isReversed isInline w="100%" maxW="340px" m="auto">
                  <Button
                    w="100%"
                    id="add-order"
                    color="#FFFFFF"
                    bg="success.500"
                    _hover={{ background: "#0F8D45" }}
                    onClick={(e) => onSubmit(e)}
                    isLoading={isLoading}
                    loadingText="Submitting"
                    rightIcon={IoIosArrowForward}
                    fontWeight="bold"
                  >
                    Add order details
                  </Button>
                  {/* <Button
                    id="save-customer"
                    variant="outline"
                    variantColor="primary"
                    _hover={{ bg: "primary.100" }}
                    onClick={(e) => onSubmit(e)}
                    isLoading={isLoading}
                    loadingText="Submitting"
                    leftIcon={FaAddressCard}
                  >
                    Save & Close
                  </Button> */}
                </Stack>
              )}
              {step === "2" && !successMessage && (
                <Stack w="100%">
                  <Button
                    m="auto"
                    w="100%"
                    fontWeight="bold"
                    maxW="340px"
                    variant="solid"
                    variantColor="success"
                    _hover={{ bg: "success.600" }}
                    type="submit"
                    isLoading={isLoading}
                    loadingText="Submitting"
                    leftIcon={FaLink}
                    onClick={initiateCreateTransaction}
                  >
                    Create Link
                  </Button>
                </Stack>
              )}
              {successMessage ? (
                <Button
                  onClick={() => {
                    reInitForm();
                  }}
                >
                  Close
                </Button>
              ) : (
                <Button
                  width="100%"
                  _hover={{ background: "#f56767" }}
                  maxW="340px"
                  onClick={() => {
                    reInitForm();
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};

export default NewPaymentLink;
